import { useState, useEffect, useCallback } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { CheckCircle, XCircle, Loader2, CheckCircle2 } from 'lucide-react'
import { listBackgrounds } from '@/services/api/qualification'
import { adminApi } from '@/services/api/admin'
import { teacherImportApi } from '@/services/api/teacher'
import type { Background } from '@/types/qualification'

interface FacultyOption {
  id: string
  name: string
  code: string
  is_active: boolean
}

interface Teacher {
  id: string
  email: string
  first_name: string
  last_name: string
  phone: string | null
  teacher_code: string | null
  is_active: boolean
  is_parsed_verified: boolean
  parsing_confidence: number
  background_ids: string[]
  faculty_id: string | null
  faculty_name: string | null
  notes: string | null
  accept_decline_ratio: number
  total_accepts: number
  total_declines: number
  created_at: string
  updated_at: string
  timetable_entry_count: number
  has_timetable: boolean
}

interface TeacherListResponse {
  teachers: Teacher[]
  total: number
  has_more: boolean
}

export function AdminTeacherListPage() {
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [backgrounds, setBackgrounds] = useState<Background[]>([])
  const [faculties, setFaculties] = useState<FacultyOption[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [validationFilter, setValidationFilter] = useState<'all' | 'confirmed' | 'unconfirmed'>('all')
  const [selectedTeachers, setSelectedTeachers] = useState<Set<string>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [minRatio, setMinRatio] = useState<number | null>(null)
  const [maxRatio, setMaxRatio] = useState<number | null>(null)
  const [minAccepts, setMinAccepts] = useState<number | null>(null)
  const [subSort, setSubSort] = useState<'accepts' | 'declines' | 'ratio' | ''>('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null)
  const [editBackgroundIds, setEditBackgroundIds] = useState<string[]>([])
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [showFacultyModal, setShowFacultyModal] = useState(false)
  const [editFacultyId, setEditFacultyId] = useState<string | null>(null)

  const fetchTeachers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (validationFilter !== 'all') params.set('validation_status', validationFilter)
      if (minRatio !== null) params.set('min_ratio', String(minRatio))
      if (maxRatio !== null) params.set('max_ratio', String(maxRatio))
      if (minAccepts !== null) params.set('min_accepts', String(minAccepts))
      if (subSort) params.set('sort_by', subSort === 'accepts' ? 'total_accepts' : subSort === 'declines' ? 'total_declines' : 'last_name')
      params.set('limit', '100')
      const [teachersRes, bgRes, facRes] = await Promise.all([
        handleApiRequest<TeacherListResponse>(apiClient.get(`/api/v1/admin/teachers?${params}`)),
        listBackgrounds(),
        teacherImportApi.listFaculties(true),
      ])
      setTeachers(teachersRes.teachers)
      setBackgrounds(bgRes)
      setFaculties(facRes)
      setError(null)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Lehrer konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }, [validationFilter, minRatio, maxRatio, minAccepts, subSort])

  useEffect(() => { fetchTeachers() }, [fetchTeachers])

  const handleToggleValidation = async (teacherId: string, isConfirmed: boolean) => {
    setUpdating(teacherId)
    setError(null)
    try {
      await handleApiRequest(apiClient.put(`/api/v1/admin/teachers/${teacherId}/validation`, { is_confirmed: isConfirmed }))
      setSuccess(isConfirmed ? 'Als bestatigt markiert' : 'Bestatigung zuruckgesetzt')
      fetchTeachers()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' }))
    } finally {
      setUpdating(null)
    }
  }

  const handleBulkValidation = async (isConfirmed: boolean) => {
    if (selectedTeachers.size === 0) return
    setError(null)
    try {
      await handleApiRequest(apiClient.put('/api/v1/admin/teachers/bulk-validation', {
        teacher_ids: Array.from(selectedTeachers),
        is_confirmed: isConfirmed,
      }))
      setSuccess(`${selectedTeachers.size} Lehrer aktualisiert`)
      setSelectedTeachers(new Set())
      fetchTeachers()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' }))
    }
  }

  const openEditModal = (teacher: Teacher) => {
    setEditingTeacher(teacher)
    setEditBackgroundIds(teacher.background_ids || [])
    setShowEditModal(true)
  }

  const openFacultyModal = (teacher: Teacher) => {
    setEditingTeacher(teacher)
    setEditFacultyId(teacher.faculty_id)
    setShowFacultyModal(true)
  }

  const handleUpdateBackgrounds = async () => {
    if (!editingTeacher) return
    setEditSubmitting(true)
    setError(null)
    try {
      await adminApi.updateTeacher(editingTeacher.id, { background_ids: editBackgroundIds })
      setSuccess('Hintergründe aktualisiert')
      setShowEditModal(false)
      setEditingTeacher(null)
      fetchTeachers()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' }))
    } finally {
      setEditSubmitting(false)
    }
  }

  const handleUpdateFaculty = async () => {
    if (!editingTeacher) return
    setEditSubmitting(true)
    setError(null)
    try {
      await adminApi.updateTeacher(editingTeacher.id, { faculty_id: editFacultyId || undefined })
      setSuccess('Fakultät aktualisiert')
      setShowFacultyModal(false)
      setEditingTeacher(null)
      fetchTeachers()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' }))
    } finally {
      setEditSubmitting(false)
    }
  }

  const getBackgroundNames = (bgIds: string[]) => {
    if (!bgIds || bgIds.length === 0) return '—'
    const names = bgIds.map(id => {
      const bg = backgrounds.find(b => b.id === id)
      return bg ? bg.name : id
    })
    return [...new Set(names)].join(', ')
  }

  const getFacultyName = (facultyId: string | null) => {
    if (!facultyId) return '—'
    const faculty = faculties.find(f => f.id === facultyId)
    return faculty ? faculty.name : facultyId
  }

  const toggleSelect = (id: string) => {
    setSelectedTeachers(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedTeachers.size === teachers.length) {
      setSelectedTeachers(new Set())
    } else {
      setSelectedTeachers(new Set(teachers.map(t => t.id)))
    }
  }

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-end mb-6">
        <div className="flex gap-2">
          <select
            value={validationFilter}
            onChange={e => setValidationFilter(e.target.value as 'all' | 'confirmed' | 'unconfirmed')}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Alle</option>
            <option value="confirmed">Bestatigt</option>
            <option value="unconfirmed">Unbestatigt</option>
          </select>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg text-sm ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}>
            Erweiterte Filter
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Accept/Decline-Filter</h3>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min. Ratio (%)</label>
              <input type="number" min={0} max={100} value={minRatio ?? ''}
                onChange={e => setMinRatio(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="—" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Max. Ratio (%)</label>
              <input type="number" min={0} max={100} value={maxRatio ?? ''}
                onChange={e => setMaxRatio(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="—" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min. Annahmen</label>
              <input type="number" min={0} value={minAccepts ?? ''}
                onChange={e => setMinAccepts(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="—" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sortierung</label>
              <select value={subSort} onChange={e => setSubSort(e.target.value as typeof subSort)}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="">Standard (Name)</option>
                <option value="accepts">Annahmen ↓</option>
                <option value="declines">Ablehnungen ↓</option>
                <option value="ratio">Ratio ↑</option>
              </select>
            </div>
          </div>
          {((minRatio !== null || maxRatio !== null || minAccepts !== null || subSort) && (
            <button onClick={() => { setMinRatio(null); setMaxRatio(null); setMinAccepts(null); setSubSort('') }}
              className="mt-2 text-xs text-red-600 hover:underline">Filter zuruecksetzen</button>
          ))}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>
      )}

      {selectedTeachers.size > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-4">
          <span className="text-sm font-medium text-blue-700">{selectedTeachers.size} ausgewahlt</span>
          <button
            onClick={() => handleBulkValidation(true)}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
          >
            Als bestatigt markieren
          </button>
          <button
            onClick={() => handleBulkValidation(false)}
            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
          >
            Bestatigung aufheben
          </button>
          <button
            onClick={() => setSelectedTeachers(new Set())}
            className="px-3 py-1.5 text-gray-500 hover:text-gray-700 text-sm"
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center text-gray-500">Laden...</div>
      ) : teachers.length === 0 ? (
        <div className="p-8 text-center bg-white rounded-lg border text-gray-500">
          Keine Lehrer gefunden
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-max divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <input
                    type="checkbox"
                    checked={teachers.length > 0 && selectedTeachers.size === teachers.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lehrer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fakultat</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hintergrund</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stundenplan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ratio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">A/N</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teachers.map(teacher => (
                <tr key={teacher.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTeachers.has(teacher.id)}
                      onChange={() => toggleSelect(teacher.id)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-4 py-3">
                    {teacher.is_parsed_verified ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle className="w-4 h-4" /> Bestatigt
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 text-xs">
                        <XCircle className="w-4 h-4" /> Unbestatigt
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {teacher.last_name}, {teacher.first_name}
                    </div>
                    <div className="text-xs text-gray-500">{teacher.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                    {teacher.teacher_code || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {getFacultyName(teacher.faculty_id)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {getBackgroundNames(teacher.background_ids)}
                  </td>
                  <td className="px-4 py-3">
                    {teacher.has_timetable ? (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-xs text-green-700 font-medium">
                          {teacher.timetable_entry_count} Einträge
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                        <span className="text-xs text-gray-400">—</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${teacher.parsing_confidence >= 80 ? 'bg-green-500' : teacher.parsing_confidence >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(teacher.parsing_confidence, 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{teacher.parsing_confidence}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <span className={`font-medium ${teacher.accept_decline_ratio >= 70 ? 'text-green-600' : teacher.accept_decline_ratio >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {teacher.accept_decline_ratio.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    <span className="text-green-600">{teacher.total_accepts}</span> / <span className="text-red-600">{teacher.total_declines}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(teacher)}
                        className="px-3 py-1.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Hintergrund
                      </button>
                      <button
                        onClick={() => openFacultyModal(teacher)}
                        className="px-3 py-1.5 rounded text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        Fakultat
                      </button>
                      <button
                        disabled={updating === teacher.id}
                        onClick={() => handleToggleValidation(teacher.id, !teacher.is_parsed_verified)}
                        className={`px-3 py-1.5 rounded text-xs font-medium ${
                          teacher.is_parsed_verified
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        } disabled:opacity-50`}
                      >
                        {updating === teacher.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : teacher.is_parsed_verified ? (
                          'Zurucksetzen'
                        ) : (
                          'Bestatigen'
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEditModal && editingTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-bold mb-4">
              Hintergründe für {editingTeacher.first_name} {editingTeacher.last_name}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hintergründe auswählen
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {backgrounds.filter(bg => bg.is_active).map(bg => (
                  <label key={bg.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editBackgroundIds.includes(bg.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditBackgroundIds([...editBackgroundIds, bg.id])
                        } else {
                          setEditBackgroundIds(editBackgroundIds.filter(id => id !== bg.id))
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-900">{bg.name}</span>
                    {bg.code && <span className="text-xs text-gray-500">({bg.code})</span>}
                  </label>
                ))}
              </div>
            </div>
            {error && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">{error}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowEditModal(false); setEditingTeacher(null); setError(null) }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={editSubmitting}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpdateBackgrounds}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFacultyModal && editingTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-bold mb-4">
              Fakultät für {editingTeacher.first_name} {editingTeacher.last_name}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fakultät auswählen
              </label>
              <select
                value={editFacultyId || ''}
                onChange={(e) => setEditFacultyId(e.target.value || null)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Keine Fakultät</option>
                {faculties.filter(f => f.is_active).map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            {error && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">{error}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowFacultyModal(false); setEditingTeacher(null); setError(null) }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={editSubmitting}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpdateFaculty}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={editSubmitting}
              >
                {editSubmitting ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
